/**
 * **Test-only overlays go through the schema check** (`resolution-events` 1.4). A shipped entry is
 * validated by the sidecar gate on load, but an entry a test writes by hand reaches the engine
 * unvalidated, and a malformed one can still run: a `chose` predicate with the wrong field names
 * reads `chosen[undefined] === undefined` and evaluates true. Under `bun test` (the preload sets
 * `LVTT_CHECK_OVERLAY_ENTRIES=1`) every entry the loader gathers is checked against
 * {@link parseOverlayEntry} once, and a failure throws naming the entry. Off everywhere else.
 */
import type { OverlayEntry } from "./atoms";
import { parseOverlayEntry } from "./schema";

const CHECK = typeof process !== "undefined" && process.env.LVTT_CHECK_OVERLAY_ENTRIES === "1";
const checked = new WeakSet<OverlayEntry>();

/** The entry, unchanged; under the test flag, schema-checked once per entry object. */
export function checkedEntry(entry: OverlayEntry | undefined): OverlayEntry | undefined {
  if (!CHECK || entry === undefined || checked.has(entry)) return entry;
  const r = parseOverlayEntry(entry);
  if (!r.success) throw new Error(`overlay entry "${entry.id}" fails the schema check: ${r.error.message}`);
  checked.add(entry);
  return entry;
}

/**
 * Exempt a fixture that is malformed on purpose (a test of how a caller survives a bad entry)
 * from the check. Returns the entry, so a fixture reads `allowMalformedEntry({ ... })`.
 */
export function allowMalformedEntry<T extends OverlayEntry>(entry: T): T {
  checked.add(entry);
  return entry;
}
